<template>
  <!-- basic modal -->
  <b-modal
    id="create-invoice-modal"
    :title="$t('create_invoices')"
    size="md"
    :no-close-on-backdrop="true"
    cancel-variant="outline-secondary"
    :cancel-title="$t('cancel')"
    :ok-title="$t('create')"
    @show="onModalOpen"
    @hidden="resetModal"
    @ok="handleCreate"
  >
    <b-form
      ref="form"
      @submit.stop.prevent="handleSubmit"
    >
      <b-row>
        <b-col cols="12">
          <b-row
            ref="row"
          >
            <b-col md="6">
              <b-form-group
                label="Start"
                label-for="start"
              >
                <flat-pickr
                  v-model="fromStr"
                  class="form-control"
                  :config="{ time_24hr: true }"
                  @on-change="(date) => { setDate(date, 'start') }"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                label="End"
                label-for="end"
              >
                <flat-pickr
                  v-model="toStr"
                  class="form-control"
                  :config="{ minDate: from, time_24hr: true }"
                  @on-change="(date) => { setDate(date, 'end') }"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <hr>
            </b-col>
          </b-row>
          <b-row v-if="appConfig.type === 'hw-admin'">
            <b-col cols="4">
              <p>Lisää jakso ylityöt</p>
            </b-col>
            <b-col cols="2">
              <b-form-checkbox
                v-model="addPeriodOvertime"
                switch
                inline
              />
            </b-col>
            <b-col v-if="addPeriodOvertime" cols="4">
              <p>Erillisenä laskuna</p>
            </b-col>
            <b-col v-if="addPeriodOvertime" cols="2">
              <b-form-checkbox
                v-model="asSeperateInvoice"
                switch
                inline
              />
            </b-col>
          </b-row>
          <b-row v-if="addPeriodOvertime && periodFrom && periodTo">
            <b-col cols="6">
              <p>Jakso työ ajalta: {{ periodFrom.toLocaleDateString('fi', {month: 'numeric', day: 'numeric'}) }} - {{ periodTo.toLocaleDateString('fi', {month: 'numeric', day: 'numeric'}) }}</p>
            </b-col>
            <b-col cols="6">
              <!-- Input a float value -->
              <b-form-group
                label="Jakso työ tunnit"
                label-for="tunnit"
              >
                <b-form-input
                  id="jaksotyo-tunnit"
                  v-model="maxHours"
                  type="number"
                  step="0.01"
                  min="0"
                  :state="maxHours > 0"
                  :class="{ 'is-invalid': maxHours < 0 || !maxHours}"
                  required
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row
        v-for="(item, index) in areas"
        :key="index"
      >
        <b-col
          md="4"
        >
          <p style="text-transform: capitalize">
            {{ item.name }}
          </p>
        </b-col>
        <b-col
          md="2"
        >
          <p>
            {{ `${item.shifts.length} ${item.shifts.length === 1 ? 'vuoro' : 'vuoroa'}` }}
          </p>
        </b-col>
        <b-col
          md="2"
        >
          <b-form-checkbox
            v-model="item.showShift"
            :value="true"
            :unchecked-value="false"
          >
            {{ $t('shift') }}
          </b-form-checkbox>
        </b-col>
        <b-col
          md="3"
        >
          <b-form-checkbox
            v-model="item.showKilometrikorvaus"
            :value="true"
            :unchecked-value="false"
          >
            Kilometrikorvaus
          </b-form-checkbox>
        </b-col>
        <b-col
          md="1"
        >
          <feather-icon
            size="16"
            icon="XCircleIcon"
            class="cursor-pointer"
            @click="removeItem(index)"
          />
        </b-col>
      </b-row>
    </b-form>
  </b-modal>
</template>

<script>
import {
  BModal, BRow, BCol, BFormGroup, BForm, BFormCheckbox, BFormInput,
} from 'bootstrap-vue'
import { calculateShift, getThreeWeekPeriod } from '@/shared/services/invoice-calc'
import { fetchContract } from '@/shared/services/contractService'
import flatPickr from 'vue-flatpickr-component'
import Ripple from 'vue-ripple-directive'
import { required } from '@validations'
import { remoteConfig } from '@/shared/services/firebase'
import { getCost, getOverHoursInPeriod, createJaksoPeriodInvoice, getHoliday, getEve } from '@/shared/services/invoiceService'
import checkTempContractShift from '@/shared/services/shiftService'

export default {
  components: {
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BForm,
    BFormCheckbox,
    BFormInput,
    flatPickr,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      areas: [],
      users: [],
      required,
      from: null,
      to: null,
      fromStr: '',
      toStr: '',
      jaksoPeriod: null,
      addPeriodOvertime: false,
      asSeperateInvoice: false,
      periodFrom: null,
      periodTo: null,
      maxHours: 114.75,
    }
  },
  computed: {
    selectedAccount() {
      return this.$store.state.appAccount.selectedAccount
    },
    typeReqOptions() {
      return this.$store.state.appAccount.roles
    },
    appConfig() {
      return this.$store.state.app.appConfig || {}
    },
  },
  methods: {
    async fetchUser(userId) {
      try {
        const { data } = await this.$store.dispatch('appInvoice/fetchUser', { userId })
        return data
      } catch (e) {
        return null
      }
    },
    removeItem(index) {
      this.areas.splice(index, 1)
    },
    setDate(date, slot) {
      if (slot === 'start') {
        [this.from] = date
      }
      if (slot === 'end') {
        /* eslint-disable no-param-reassign */
        [this.to] = date
        this.to.setHours(23, 59, 59, 0)
      }
      if (this.from < this.to) {
        const period = getThreeWeekPeriod(this.from)
        this.periodFrom = period.start
        this.periodTo = period.end
        this.fetchShifts(this.from, this.to)
      }
    },
    fetchShifts(from, to) {
      const period = getThreeWeekPeriod(from)
      if(period.end <= to) {
        this.jaksoPeriod = period
      }
      this.$store.dispatch('appInvoice/fetchShifts', { from, to, accountId: this.selectedAccount.value.id })
        .then(async response => {
          const areaIds = []
          const userIds = []
          const shiftsMap = []
          this.user = []
          response.data.forEach(shift => {
            if (shift.areaId) {
              const id = shift.areaId
              if (areaIds.indexOf(id) === -1) {
                areaIds.push(id)
                shiftsMap[id] = [shift]
              } else {
                shiftsMap[id].push(shift)
              }
            }
            // Map users
            const userId = shift.assignedTo
            if (userIds.indexOf(userId) === -1) {
              userIds.push(userId)
            }
          })
          // Fetch users
          userIds.forEach(async id => {
            const user = await this.fetchUser(id)
            this.users[id] = user
          })
          this.areas = await Promise.all(areaIds.map(async id => {
            const area = await this.$store.dispatch('appInvoice/fetchArea', { id })
            return area
          }))
          // Match shifts with areas
          this.areas.forEach(area => {
            /* eslint-disable no-param-reassign */
            const shifts = shiftsMap[area.id]
            area.shifts = shifts
            area.showShift = true
            area.showKilometrikorvaus = true
            /* eslint-enable */
          })
        })
        .catch(() => {
          this.$emit('error')
        })
    },
    onModalOpen() {
      this.from = null
      this.to = null
      this.fromStr = ''
      this.toStr = ''
      this.areas = []
      this.asSeperateInvoice = false
      this.addPeriodOvertime = false
      this.periodFrom = null
      this.periodTo = null
    },
    resetModal() {
    },
    async handleCreate() {
      await remoteConfig.fetchAndActivate()
      
      const invoices = []
      const holidaysConfig = JSON.parse(remoteConfig.getValue('holidays').asString())
      const holidayArrays = Object.entries(holidaysConfig).map(year => year[1])
      const holidays = [].concat(...holidayArrays)

      // Get eves
      const evesConfig = JSON.parse(remoteConfig.getValue('eves').asString())
      const evesArray = Object.entries(evesConfig).map(year => year[1])
      const eves = [].concat(...evesArray)

      const today = new Date()
      const dueDate = new Date(today)
      dueDate.setDate(dueDate.getDate() + 21)
      let periodShifts = []
      // Get jakso period overtime
      if(this.addPeriodOvertime && this.periodFrom && this.periodTo) {
          const result = await this.$store.dispatch('appInvoice/fetchShifts', { from: this.jaksoPeriod.start, to: this.jaksoPeriod.end, accountId: this.selectedAccount.value.id })
          periodShifts = result.data
      }

      // All these categories have VAT enabled always
      const hasVAT = category => {
        if(category === 'kotihoito') {
          return true
        }
        if(category === 'hoitokoti') {
          return true
        }
        if(category === 'palvelutalo') {
          return true
        }
        return false
      }
      await Promise.all(this.areas.map(async area => {
        const kilometrikorvausList = []
        const contract = await this.$store.dispatch('appInvoice/fetchContract', { id: area.contract })
        const account = this.selectedAccount.value
        const name = (area.showKilometrikorvaus && !area.showShift) ? `${area.name} - kilometrikorvaukset` : area.name
        const VAT = hasVAT(area.category)
        let invoice = {
          areaId: area.id,
          date: today,
          total: 0,
          items: [],
          from: this.from,
          to: this.to,
          status: 'pending',
          name,
          accountId: account.id,
          customerNumber: account.customerNumber || '',
          factoring: !!this.selectedAccount.value.factoring,
          dueDate,
          VAT,
          shifts: area.shifts.map(shift => shift.id),
        }
        await Promise.all(area.shifts.map(async shift => {
          const {id} = shift
          const userId = shift.assignedTo
          if (userId) {
            const user = this.users[userId]
            let isJaksoPeriodShift = false
            let finalContract = contract
            const matchedTempContract = checkTempContractShift({ ...shift, user })
            if (matchedTempContract && matchedTempContract.contractId) {
              finalContract = await fetchContract(matchedTempContract.contractId)
              isJaksoPeriodShift = true
            }
            const baseCost = getCost(finalContract, user)
            const unpaidTime = finalContract.unpaidTime || 0
            const isHoliday = await getHoliday(shift.startStr, shift.endStr, holidays)
            const isEveningHoliday = await getEve(shift.end, shift.endStr, eves)
            const report = calculateShift(shift.start, shift.end, baseCost, isHoliday, isEveningHoliday, unpaidTime, account.accountType === 'public', isJaksoPeriodShift)
            let title = user.fullName
            title = `${title.trim()} ${shift.start.toLocaleString('fi', { month: 'numeric', day: 'numeric' })}`
            const startTime = shift.start.toLocaleString('fi', { hour: 'numeric', minute: 'numeric' })
            const endTime = shift.end.toLocaleString('fi', { hour: 'numeric', minute: 'numeric' })
            const shiftPeriod = `klo ${startTime} - ${endTime}`
            if (area.showShift) {
              if (report.perus) {
                const qty = report.perus.hours
                const cost = report.lauantaiLisa && finalContract.price.saturdayPrice ? finalContract.price.saturdayPrice : baseCost
                const total = cost * qty
                invoice.total += total
                invoice.items.push({
                  title: `${title} ${shiftPeriod}`,
                  qty,
                  unit: 'tuntia',
                  cost,
                  total,
                  shiftId: id,
                })
              }
              if (report.iltaLisa && !finalContract.noEvening  && !finalContract.priceIncludesLisat) {
                const qty = report.iltaLisa.hours
                const cost = account.isJoiku
                  ? report.iltaLisa.price * 1.6
                  : report.iltaLisa.price
                const total = cost * qty
                invoice.total += total
                invoice.items.push({
                  title: `${title} iltalisä`,
                  qty,
                  unit: 'tuntia',
                  cost,
                  shiftId: id,
                  total,
                })
              }
              if (report.yoLisa && !finalContract.priceIncludesLisat) {
                const qty = report.yoLisa.hours
                // eslint-disable-next-line no-nested-ternary
                const cost = isJaksoPeriodShift
                  ? baseCost * 0.40
                  : account.isJoiku
                    ? report.yoLisa.price * 1.6
                    : report.yoLisa.price
                const total = cost * qty
                invoice.total += total
                invoice.items.push({
                  title: `${title} yölisä`,
                  qty,
                  unit: 'tuntia',
                  cost,
                  shiftId: id,
                  total,
                })
              }
              if (report.lauantaiLisa && !finalContract.priceIncludesLisat && !finalContract.price.saturdayPrice) {
                const qty = report.lauantaiLisa.hours
                const cost = account.isJoiku
                  ? report.lauantaiLisa.price * 1.6
                  : report.lauantaiLisa.price
                const total = cost * qty
                invoice.total += total
                invoice.items.push({
                  title: `${title} lauantailisä`,
                  qty,
                  unit: 'tuntia',
                  cost,
                  shiftId: id,
                  total,
                })
              }
              if (report.sunnuntaiLisa && !finalContract.priceIncludesLisat) {
                const qty = report.sunnuntaiLisa.hours
                const cost = account.isJoiku
                  ? baseCost * 1.6
                  : baseCost
                const total = cost * qty
                invoice.total += total
                const lisaTitle = report.isHoliday ? `${title} pyhälisä` : `${title} sunnuntailisä`
                invoice.items.push({
                  title: lisaTitle,
                  qty,
                  unit: 'tuntia',
                  cost,
                  shiftId: id,
                  total,
                })
              }
              if (report.ylityo && !finalContract.noOverHours && !isJaksoPeriodShift) {
                if (report.ylityo.first) {
                  const qty = report.ylityo.first.hours
                  const cost = baseCost * 0.5
                  const total = cost * qty
                  invoice.total += total
                  invoice.items.push({
                    title: `${title} ylityö 50%`,
                    qty,
                    unit: 'tuntia',
                    cost,
                    shiftId: id,
                    total,
                  })
                }
                if (report.ylityo.second) {
                  const qty = report.ylityo.second.hours
                  const cost = baseCost
                  const total = cost * qty
                  invoice.total += total
                  invoice.items.push({
                    title: `${title} ylityö 100%`,
                    qty,
                    unit: 'tuntia',
                    cost,
                    shiftId: id,
                    total,
                  })
                }
              }
            }
            if (area.showKilometrikorvaus && shift.kilometrikorvaus && shift.kilometrikorvaus > 0) {
              const qty = shift.kilometrikorvaus
              const cost = 0.53 // In EUR, km allowance 2023, see more https://www.veronmaksajat.fi/Palkka-ja-elake/Kilometrikorvaukset/kilometrikorvaukset-2023/
              const total = cost * qty
              const item = {
                title: `${title} kilometrikorvaus`,
                qty,
                unit: 'km',
                cost,
                shiftId: id,
                total,
              }
              kilometrikorvausList.push(item)
              invoice.total += total
            }
            invoice.total = Math.round(invoice.total * 100) / 100
          }
        }))

        if(!this.asSeperateInvoice && periodShifts.length > 0) {
          const users = []
          Object.keys(this.users).forEach(key => {
            users.push(this.users[key])
          })
          const result = getOverHoursInPeriod(invoice, this.jaksoPeriod, this.maxHours, users, periodShifts, contract, [area.id])
          invoice = result.invoice
        }
        invoice.items = invoice.items.concat(kilometrikorvausList)
        invoices.push(invoice)
      }))
      // Create jakso period overtime invoice
      if(this.asSeperateInvoice && periodShifts.length > 0) {
        // Get userIds of all users of shifts
        const userIds = []
        const areaIds = []
        const users = []
        periodShifts.forEach(shift => {
          const userId = shift.assignedTo
          if (userIds.indexOf(userId) === -1) {
            userIds.push(userId)
          }
          const {areaId} = shift
          if (areaIds.indexOf(areaId) === -1) {
            areaIds.push(areaId)
          }
        })
        // Fetch users
        userIds.forEach(async id => {
            const user = await this.fetchUser(id)
            users.push(user)
        })
        const areas = await Promise.all(areaIds.map(async id => {
          const area = await this.$store.dispatch('appInvoice/fetchArea', { id })
          return area
        }))
        const contract = await this.$store.dispatch('appInvoice/fetchContract', { id: areas[0].contract })
        const account = this.selectedAccount.value
        const VAT = hasVAT(areas[0].category)
        const invoice = createJaksoPeriodInvoice(this.jaksoPeriod, account, periodShifts, users, this.maxHours, contract, areas, VAT)
        invoices.push(invoice)
      }
      await Promise.all(invoices.map(async invoice => {
        await this.$store.dispatch('appInvoice/createInvoice', { invoice })
      }))
      this.$emit('created')
    },
  },
}
</script>

<style lang="scss">
@import '@hw-ui/scss/vue/libs/vue-flatpicker.scss';
.modal-dialog {
  max-width: 700px;
}
</style>
